import React from 'react';
import { DefaultButton, Text, Icon, mergeStyleSets, Stack } from '@fluentui/react';
import { useIntl } from '@ysoft/react-intl';
import { useMsal } from '@azure/msal-react';
import { omniTheme } from '../theme';
import { isAuthMocked } from '../config/config';
import { useConfigContext } from '../config/configContext';

const classNames = mergeStyleSets({
	auth_error__page: {
		textAlign: 'center',
		color: omniTheme.palette.neutralSecondary,
		height: 'calc(100vh - 3rem)',
	},
	heading: {
		margin: 0,
		heading: 0,
	},
	auth_error__icon_bg: {
		height: '5rem',
		width: '5rem',
		borderRadius: '5rem',
		backgroundColor: omniTheme.palette.neutralLighter,
	},
	auth_error__icon: {
		color: omniTheme.palette.themePrimary,
	},
	auth_error__logout: {
		marginBlockStart: '1.5rem',
	},
});

const headerStyle = {
	root: {
		padding: '0.3rem 1rem',
		background: omniTheme.palette.themePrimary,
		color: omniTheme.palette.white,
	},
};

export type AuthErrorVariant = 'authError' | 'tenantNotAllowed';

interface AuthErrorPageProps {
	variant?: AuthErrorVariant;
}

const useLogout = () => {
	const { config } = useConfigContext();
	const { instance } = useMsal();

	return async () => {
		if (isAuthMocked) {
			return;
		}
		try {
			await instance.logoutRedirect({
				postLogoutRedirectUri: config.msal.redirectUri,
			});
		} catch (error) {
			console.error('Logout error:', error);
		}
	};
};

const AuthErrorPage: React.FC<AuthErrorPageProps> = ({ variant = 'authError' }) => {
	const { t } = useIntl();
	const logout = useLogout();

	const lineOneKey =
		variant === 'tenantNotAllowed'
			? 'general__tenant_not_allowed_line_one'
			: 'general__auth_error_line_one';
	const lineTwoKey =
		variant === 'tenantNotAllowed'
			? 'general__tenant_not_allowed_line_two'
			: 'general__auth_error_line_two';

	return (
		<>
			<Stack
				horizontal
				styles={headerStyle}
				data-testid="layout-header"
				horizontalAlign="space-between"
				verticalAlign="center"
			>
				<Stack.Item>
					<h1 className={classNames.heading}>{t('app__title')}</h1>
				</Stack.Item>
			</Stack>
			<Stack
				data-testid="auth-error__page"
				horizontalAlign="center"
				verticalAlign="center"
				className={classNames.auth_error__page}
			>
				<Stack
					className={classNames.auth_error__icon_bg}
					horizontalAlign="center"
					verticalAlign="center"
				>
					<Stack.Item align="center">
						<Text variant="superLarge">
							<Icon
								className={classNames.auth_error__icon}
								data-testid="header_warning-icon"
								iconName="Lock"
							/>
						</Text>
					</Stack.Item>
				</Stack>
				<Stack>
					<Text variant="xxLarge">
						<p>{t(lineOneKey)}</p>
						<p>{t(lineTwoKey)}</p>
					</Text>
				</Stack>
				{!isAuthMocked && (
					<Stack.Item className={classNames.auth_error__logout}>
						<DefaultButton
							data-testid="auth-error__logout-button"
							onClick={logout}
						>
							{t('app__logout')}
						</DefaultButton>
					</Stack.Item>
				)}
			</Stack>
		</>
	);
};

export default AuthErrorPage;
